/*
 * A guided tour Ada is offering, as a card the operator chooses to start.
 *
 * Offered, never launched. Being yanked into a panel because you asked a
 * question is the behaviour that makes product tours something people close
 * without reading — the card says what it will show and how long it is, and
 * waits.
 *
 * Nothing here is rendered from model output as markup: the stops are ids from
 * a closed enum, and every visual decision is made in this file.
 *
 * Once started, the card says where the tour stands rather than offering it
 * afresh: paused at which stop, with the way back in, or finished.
 */

import { FiCompass, FiArrowRight, FiCheck } from "react-icons/fi";
import { startTour, useTour, walkthroughOf, endingOf } from "../tour/tourStore";
import TourProgress from "../tour/TourProgress";
import { VIEW_CONSOLE, navigateTo } from "../appView";
import { navLabel } from "../navigation";

export default function TourCard({ artifact, nodeId }) {
  const { tour, ends } = useTour();
  const spec = artifact.spec || {};
  const stops = Array.isArray(spec.stops) ? spec.stops.filter((s) => s && s.control) : [];
  if (!stops.length) return null;

  const panel = spec.panel || "obfuscation";
  const panelLabel = navLabel(panel) || "the console";

  // Ada is open, so a stored walkthrough is not running: it is paused.
  const mine = walkthroughOf(tour, { origin: artifact.id });
  const ended = !mine && endingOf(ends, { origin: artifact.id });
  const other = tour && !mine;

  const begin = () => {
    /* Explanation stops carry no verification — there is nothing to change, so
       nothing to confirm. The overlay advances them on Next. */
    const steps = stops.map((s, i) => ({
      id: `${artifact.id}-${i}`,
      panel,
      panelLabel,
      target: s.control,
      title: controlTitle(s.control),
      instruction: "",
      why: String(s.note || ""),
      verify: null,
      verifyPath: null,
      // Explanation, not change: the walkthrough must not tell the operator to
      // set anything at a stop that exists to be read.
      kind: "explain",
      index: i,
    }));
    if (!startTour(steps, { kind: "tour", id: artifact.id })) return;
    navigateTo(VIEW_CONSOLE, { node: nodeId, panel, from: "console" });
  };

  const stopState = (i) => (!mine ? "" : i < mine.at ? "is-done" : i === mine.at ? "is-now" : "");

  return (
    <div className="tc-card">
      <Styles />
      <div className="tc-head">
        <span className="tc-mark" aria-hidden="true"><FiCompass size={15} /></span>
        <div className="tc-id">
          <div className="tc-title">{artifact.title}</div>
          <div className="tc-sub">
            {ended
              ? `You finished this tour of ${panelLabel}.`
              : `${stops.length} stops in ${panelLabel} · about ${Math.max(1, Math.round(stops.length * 0.4))} min`}
          </div>
        </div>
      </div>

      {spec.intro && <p className="tc-intro">{spec.intro}</p>}

      <ol className="tc-stops">
        {stops.map((s, i) => (
          <li key={i} className={stopState(i)}>
            <span>{stopState(i) === "is-done" ? <FiCheck size={10} /> : i + 1}</span>
            {controlTitle(s.control)}
          </li>
        ))}
      </ol>

      {mine ? (
        <TourProgress tour={mine} nodeId={nodeId} noun="stop" what="tour" />
      ) : (
        <>
          <button className="tc-go" onClick={begin}>
            {ended ? "Take it again" : "Take the tour"} <FiArrowRight size={14} />
          </button>
          {other && <p className="tc-note">Starting it ends the walkthrough you paused.</p>}
        </>
      )}
    </div>
  );
}

/* The operator's name for a control, matching the panel's own heading. */
const TITLES = {
  "obf.wire": "Hide the protocol",
  "obf.pad": "Pad packet sizes",
  "obf.blend": "Spread across links",
  "obf.chaff": "Cover traffic",
  "obf.jitter": "Timing randomisation",
  "obf.strict": "Fixed rate",
  "obf.anchors": "Spread across exits",
  "obf.rotation": "Automatic re-keying",
};

const controlTitle = (id) => TITLES[id] || id;

const Styles = () => (
  <style>{`
.tc-card{
  border:1px solid rgba(103,232,249,.30);border-radius:14px;padding:16px 17px;
  background:linear-gradient(180deg,rgba(103,232,249,.07),rgba(129,140,248,.05));
}
.tc-head{display:flex;align-items:flex-start;gap:12px}
.tc-mark{
  flex:none;width:32px;height:32px;border-radius:10px;display:grid;place-items:center;
  background:rgba(103,232,249,.14);border:1px solid rgba(103,232,249,.32);color:#67E8F9;
}
.tc-id{min-width:0}
.tc-title{font-size:15.5px;font-weight:650;color:#F1F5F9;letter-spacing:-.01em}
.tc-sub{font-size:12.5px;color:#8891AC;margin-top:2px}
.tc-intro{margin:13px 0 0;font-size:14px;line-height:1.6;color:#C9CFE2}
.tc-stops{
  list-style:none;margin:13px 0 0;padding:0;display:flex;flex-wrap:wrap;gap:7px;
}
.tc-stops li{
  display:inline-flex;align-items:center;gap:7px;
  font-size:12.5px;color:#A8B0C6;
  border-radius:999px;padding:5px 11px 5px 6px;
}
.tc-stops li span{
  width:17px;height:17px;border-radius:50%;display:grid;place-items:center;flex:none;
  background:rgba(103,232,249,.16);color:#67E8F9;font-size:10.5px;font-weight:700;
}
.tc-stops li.is-done{color:#94A3B8}
.tc-stops li.is-done span{background:rgba(52,211,153,.18);color:#6EE7B7}
.tc-stops li.is-now{color:#F1F5F9;font-weight:650;background:rgba(103,232,249,.12)}
.tc-go{
  margin-top:15px;display:inline-flex;align-items:center;gap:8px;cursor:pointer;
  border:0;border-radius:10px;padding:10px 18px;font:inherit;font-size:14px;font-weight:700;line-height:1;
  color:#0B0C11;background:linear-gradient(135deg,#67E8F9,#818CF8);
}
.tc-go:hover{filter:brightness(1.06)}
.tc-note{margin:9px 0 0;font-size:12.5px;color:#8891AC}
`}</style>
);
